import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import Cookies from "js-cookie";

import { baseURL } from "../../../config/Endpoints.js";
import { getApi } from "../../utils/api.ts";

export type EmployeeAllocationDetailRow = {
  projectName: string;
  designation: string;
  allocatedHours: number;
  deliveryLeadName: string;
  projectManagerName: string;
};

async function fetchEmployeeAllocations(
  employeeId: string,
): Promise<EmployeeAllocationDetailRow[]> {
  const token = Cookies.get("accessToken");
  const url = `${baseURL}/api/v2/allocations/employee/${encodeURIComponent(employeeId)}`;
  const { data } = await getApi<EmployeeAllocationDetailRow[]>(url, token);
  return Array.isArray(data) ? data : [];
}

/**
 * Fetches GET /api/v2/allocations/employee/{employeeId} when `enabled` is true.
 */
export function useEmployeeAllocations(
  employeeId: string | null,
  enabled: boolean,
) {
  const [rows, setRows] = useState<EmployeeAllocationDetailRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (enabled && employeeId) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [employeeId, enabled]);

  useEffect(() => {
    if (!enabled || !employeeId) {
      setRows([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setError(null);
      try {
        const data = await fetchEmployeeAllocations(employeeId);
        if (!cancelled) {
          setRows(data);
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error
              ? e.message
              : "Failed to load allocation details.",
          );
          setRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [employeeId, enabled]);

  const refetch = useCallback(async () => {
    if (!employeeId || !enabled) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchEmployeeAllocations(employeeId);
      setRows(data);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to load allocation details.",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [employeeId, enabled]);

  return { rows, loading, error, refetch };
}
